import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, CreditCard, HelpCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import T from '@/components/T';
import { useSubscriptionPlans } from '@/hooks/useSubscriptionPlans';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

interface SubscriptionPaywallProps {
  daysOverdue?: number;
}

export const SubscriptionPaywall: React.FC<SubscriptionPaywallProps> = ({ daysOverdue = 0 }) => {
  const navigate = useNavigate();
  const { plans, currentSubscription } = useSubscriptionPlans();
  
  const currentPlan = plans.find(plan => plan.id === currentSubscription?.plan_id);
  const planName = currentPlan?.name || 'Ваш план';
  const expiredDate = currentSubscription?.expires_at 
    ? format(new Date(currentSubscription.expires_at), 'd MMMM yyyy', { locale: ru })
    : null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="absolute inset-0 bg-gradient-to-br from-destructive/5 via-transparent to-destructive/10" />
      
      <Card className="relative w-full max-w-lg mx-4 border-destructive/50 shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 border-2 border-destructive/30">
            <Lock className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl font-bold text-destructive">
            <T ru="Доступ заблокирован" en="Access Blocked" />
          </CardTitle>
          <CardDescription className="text-base">
            <T 
              ru="Ваша подписка истекла и период ожидания завершён" 
              en="Your subscription has expired and grace period has ended"
            />
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Expiry info */}
          <div className="rounded-lg border border-muted bg-muted/50 p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium">
                <T ru="Информация о подписке" en="Subscription Info" />
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <p>
                <T ru="План:" en="Plan:" />{' '}
                <span className="font-semibold">{planName}</span>
              </p>
              {expiredDate && (
                <p>
                  <T ru="Дата истечения:" en="Expired on:" />{' '}
                  <span className="font-semibold text-destructive">{expiredDate}</span>
                </p>
              )}
              {daysOverdue > 0 && (
                <p>
                  <T ru="Просрочено дней:" en="Days overdue:" />{' '}
                  <span className="font-semibold text-destructive">{daysOverdue}</span>
                </p>
              )}
            </div>
          </div>

          {/* Action section */}
          <div className="space-y-3">
            <p className="text-center text-sm text-muted-foreground">
              <T 
                ru="Для продолжения работы необходимо оплатить подписку" 
                en="To continue working, you need to pay for your subscription"
              />
            </p>
            
            <Button 
              size="lg" 
              className="w-full gap-2 text-lg py-6"
              onClick={() => navigate('/subscription/payment')}
            >
              <CreditCard className="h-5 w-5" />
              <T ru="Оплатить подписку" en="Pay Subscription" />
            </Button>
          </div>

          {/* Support link */}
          <div className="pt-2 border-t">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <HelpCircle className="h-4 w-4" />
              <span>
                <T ru="Вопросы? Свяжитесь с поддержкой:" en="Questions? Contact support:" />{' '}
                <a 
                  href="mailto:support@hotel3d.io" 
                  className="text-primary hover:underline font-medium"
                >
                  support@hotel3d.io
                </a>
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
